import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
    const [search] = useSearchParams();
    const navigate = useNavigate();
    const token = search.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [state, setState] = useState("idle"); // idle | saving | done
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Both password fields must match.");
            return;
        }
        setState("saving");
        try {
            await api.post("/auth/reset-password", { token, password });
            setState("done");
        } catch (err) {
            setState("idle");
            setError(
                formatApiError(err.response?.data?.detail) ||
                    "That link is invalid or has expired.",
            );
        }
    };

    if (!token) {
        return (
            <main className="container-page py-16 max-w-md fade-up" data-testid="reset-password-page">
                <h1 className="text-3xl font-semibold text-[#1C2321] mb-3" style={{ fontFamily: "Outfit" }}>
                    Reset link is missing
                </h1>
                <p className="text-[#515B57] mb-6">
                    Please use the link from your email, or request a new one.
                </p>
                <Link
                    to="/forgot-password"
                    data-testid="reset-request-new-link"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                >
                    Get a new link
                </Link>
            </main>
        );
    }

    return (
        <main className="container-page py-16 max-w-md fade-up" data-testid="reset-password-page">
            <h1 className="text-4xl font-semibold text-[#1C2321] mb-2" style={{ fontFamily: "Outfit" }}>
                Set a new password
            </h1>
            <p className="text-[#515B57] text-lg mb-8">
                Pick something you&apos;ll remember. At least 6 characters.
            </p>

            {state === "done" ? (
                <div
                    className="card-rw p-6"
                    style={{ borderLeft: "6px solid #2D5D4E" }}
                    data-testid="reset-done"
                >
                    <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-6 h-6 text-[#3A7D5C] mt-0.5" strokeWidth={1.6} />
                        <div>
                            <h2 className="text-xl font-medium text-[#1C2321] mb-1" style={{ fontFamily: "Outfit" }}>
                                Password updated
                            </h2>
                            <p className="text-[#515B57] leading-relaxed mb-4">
                                You can sign in with your new password now.
                            </p>
                            <button
                                onClick={() => navigate("/login")}
                                data-testid="reset-go-login"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                            >
                                Go to sign in
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                    <div>
                        <label className="block text-sm font-medium text-[#1C2321] mb-2">New password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                            data-testid="reset-password-input"
                            className="w-full px-4 py-3 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] text-lg focus:outline-none focus:ring-2 focus:ring-[#2D5D4E]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[#1C2321] mb-2">Confirm new password</label>
                        <input
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                            data-testid="reset-confirm-input"
                            className="w-full px-4 py-3 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] text-lg focus:outline-none focus:ring-2 focus:ring-[#2D5D4E]"
                        />
                    </div>
                    {error && (
                        <p className="text-[#B24C4C] text-base" data-testid="reset-error">{error}</p>
                    )}
                    <button
                        type="submit"
                        disabled={state === "saving"}
                        data-testid="reset-submit-button"
                        className="w-full py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] transition-colors disabled:opacity-50"
                    >
                        {state === "saving" ? "Saving…" : "Update password"}
                    </button>
                </form>
            )}
        </main>
    );
}
